import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  useAssignAdminCardToUserMutation,
  useGetAdminCardCatalogQuery,
  useLazyGetAdminUserCardsQuery,
  useLazySearchAdminUsersQuery,
  useRemoveAdminCardInstanceMutation,
  type AdminUserCardInstance,
  type AdminUserSummary,
  type CardDefinition,
} from "../../api/respectCardsApi";
import { useConfirm } from "../../../../ui/components/ConfirmDialog";

/**
 * Cards admin → Users tab. Lets admins search by username, then assign a
 * card from the active catalog to that user, and remove existing cards
 * (CardInstance docs) from their hand/history.
 */

const card: React.CSSProperties = {
  background: "#fff",
  padding: 16,
  borderRadius: 8,
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
  marginBottom: 16,
};

const headerRow: React.CSSProperties = {
  display: "flex",
  gap: 8,
  marginBottom: 12,
  flexWrap: "wrap",
  alignItems: "center",
};

const input: React.CSSProperties = {
  flex: 1,
  minWidth: 220,
  padding: "8px 10px",
  border: "1px solid #ccc",
  borderRadius: 6,
  fontSize: "0.9rem",
};

const select: React.CSSProperties = {
  padding: "8px 10px",
  border: "1px solid #ccc",
  borderRadius: 6,
  fontSize: "0.9rem",
  background: "#fff",
};

const btn: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 6,
  border: "1px solid #4f3e76",
  background: "#f5f1ff",
  color: "#4f3e76",
  cursor: "pointer",
  fontSize: "0.85rem",
};

const dangerBtn: React.CSSProperties = {
  ...btn,
  border: "1px solid #c62828",
  background: "#ffebee",
  color: "#c62828",
};

const ghostBtn: React.CSSProperties = {
  ...btn,
  border: "1px solid #ccc",
  background: "#fff",
  color: "#333",
};

const userRow: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 10px",
  borderBottom: "1px solid #eee",
  cursor: "pointer",
};

const cardRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr auto auto auto auto",
  gap: 12,
  alignItems: "center",
  padding: "8px 10px",
  borderBottom: "1px solid #eee",
  fontSize: "0.85rem",
};

const statusChip = (status: string): React.CSSProperties => ({
  display: "inline-block",
  fontSize: "0.7rem",
  fontWeight: 600,
  padding: "2px 8px",
  borderRadius: 999,
  background:
    status === "in_hand"
      ? "rgba(34, 197, 94, 0.15)"
      : status === "played"
      ? "rgba(99, 102, 241, 0.15)"
      : status === "expired"
      ? "rgba(239, 68, 68, 0.15)"
      : "rgba(107, 114, 128, 0.15)",
  color:
    status === "in_hand"
      ? "#166534"
      : status === "played"
      ? "#3730a3"
      : status === "expired"
      ? "#991b1b"
      : "#374151",
  textTransform: "uppercase",
});

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

const UsersTab: React.FC = () => {
  const confirm = useConfirm();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<AdminUserSummary | null>(null);
  const [cardSlugToAssign, setCardSlugToAssign] = useState("");
  const [assignSource, setAssignSource] = useState<"admin" | "gift" | "event" | "milestone">(
    "admin",
  );

  const [triggerSearch, searchState] = useLazySearchAdminUsersQuery();
  const [triggerLoadUserCards, userCardsState] = useLazyGetAdminUserCardsQuery();
  const [assignCard, assignState] = useAssignAdminCardToUserMutation();
  const [removeCard, removeState] = useRemoveAdminCardInstanceMutation();
  const { data: catalogData, isLoading: catalogLoading } = useGetAdminCardCatalogQuery();

  // Debounced search.
  useEffect(() => {
    const term = searchTerm.trim();
    const timer = setTimeout(() => {
      triggerSearch({ query: term, limit: 20 });
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm, triggerSearch]);

  const activeCatalog: CardDefinition[] = useMemo(() => {
    const all = catalogData?.cards ?? [];
    return all.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [catalogData]);

  const handleSelectUser = (user: AdminUserSummary) => {
    setSelectedUser(user);
    triggerLoadUserCards(user._id);
  };

  const handleAssign = async () => {
    if (!selectedUser || !cardSlugToAssign) return;
    try {
      const res = await assignCard({
        userId: selectedUser._id,
        cardId: cardSlugToAssign,
        source: assignSource,
      }).unwrap();
      toast.success(res.message || "Card assigned.");
      setCardSlugToAssign("");
    } catch (err) {
      const msg =
        err && typeof err === "object" && "data" in err
          ? (err as { data?: { message?: string } }).data?.message
          : null;
      toast.error(msg || "Failed to assign card.");
    }
  };

  const handleRemove = async (instance: AdminUserCardInstance) => {
    if (!selectedUser) return;
    const cardName = instance.definition?.name || instance.cardId;
    const ok = await confirm({
      title: "Remove card from user?",
      message: `This will permanently delete the "${cardName}" instance (${instance.status}) from ${selectedUser.username}'s collection. This cannot be undone.`,
      confirmLabel: "Remove",
      tone: "danger",
    });
    if (!ok) return;
    try {
      const res = await removeCard({
        instanceId: instance._id,
        userId: selectedUser._id,
      }).unwrap();
      toast.success(res.message || "Card removed.");
    } catch (err) {
      const msg =
        err && typeof err === "object" && "data" in err
          ? (err as { data?: { message?: string } }).data?.message
          : null;
      toast.error(msg || "Failed to remove card.");
    }
  };

  const users = searchState.data?.users ?? [];
  const userCards = userCardsState.data?.cards ?? [];

  return (
    <>
      <section style={card}>
        <h2 style={{ fontSize: "1.1rem", color: "#333", marginBottom: 12 }}>
          Find user
        </h2>
        <div style={headerRow}>
          <input
            style={input}
            placeholder="Search by username…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchState.isFetching ? (
            <span style={{ fontSize: "0.8rem", color: "#666" }}>searching…</span>
          ) : null}
        </div>
        {searchState.error ? (
          <p style={{ color: "#a00" }}>failed to search users.</p>
        ) : null}
        <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #eee", borderRadius: 6 }}>
          {users.length === 0 ? (
            <div style={{ padding: 12, color: "#666", fontSize: "0.85rem" }}>
              {searchTerm.trim() ? "no users matched." : "type to search."}
            </div>
          ) : (
            users.map((u) => {
              const isSelected = selectedUser?._id === u._id;
              return (
                <div
                  key={u._id}
                  style={{
                    ...userRow,
                    background: isSelected ? "#f5f1ff" : "transparent",
                  }}
                  onClick={() => handleSelectUser(u)}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: "#222" }}>{u.username}</div>
                    <div style={{ fontSize: "0.78rem", color: "#666" }}>
                      {u.email ?? "no email"} · {u.role ?? "member"}
                    </div>
                  </div>
                  <button
                    type="button"
                    style={ghostBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectUser(u);
                    }}
                  >
                    {isSelected ? "selected" : "open"}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </section>

      {selectedUser ? (
        <>
          <section style={card}>
            <h2 style={{ fontSize: "1.1rem", color: "#333", marginBottom: 4 }}>
              Assign a card to{" "}
              <span style={{ color: "#4f3e76" }}>{selectedUser.username}</span>
            </h2>
            <p style={{ fontSize: "0.82rem", color: "#666", marginBottom: 12 }}>
              Creates a fresh CardInstance with status <code>in_hand</code>. The
              card's <code>unplayedValidityDays</code> still applies.
            </p>
            <div style={headerRow}>
              <select
                style={{ ...select, flex: 1, minWidth: 220 }}
                value={cardSlugToAssign}
                onChange={(e) => setCardSlugToAssign(e.target.value)}
                disabled={catalogLoading}
              >
                <option value="">— pick a card —</option>
                {activeCatalog.map((c) => (
                  <option key={c.id} value={c.slug || c.id}>
                    {c.emoji} {c.name} ({c.rarity})
                  </option>
                ))}
              </select>
              <select
                style={select}
                value={assignSource}
                onChange={(e) =>
                  setAssignSource(e.target.value as typeof assignSource)
                }
              >
                <option value="admin">source: admin</option>
                <option value="gift">source: gift</option>
                <option value="event">source: event</option>
                <option value="milestone">source: milestone</option>
              </select>
              <button
                type="button"
                style={btn}
                onClick={handleAssign}
                disabled={!cardSlugToAssign || assignState.isLoading}
              >
                {assignState.isLoading ? "assigning…" : "Assign"}
              </button>
            </div>
          </section>

          <section style={card}>
            <h2 style={{ fontSize: "1.1rem", color: "#333", marginBottom: 12 }}>
              {selectedUser.username}'s cards{" "}
              <span style={{ fontSize: "0.85rem", color: "#666", fontWeight: 400 }}>
                ({userCards.length})
              </span>
            </h2>
            {userCardsState.isFetching ? (
              <p style={{ color: "#666" }}>loading cards…</p>
            ) : userCardsState.error ? (
              <p style={{ color: "#a00" }}>failed to load user's cards.</p>
            ) : userCards.length === 0 ? (
              <p style={{ color: "#666", fontSize: "0.88rem" }}>
                this user has no cards yet.
              </p>
            ) : (
              <div>
                <div
                  style={{
                    ...cardRow,
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: "#666",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  <div>Card</div>
                  <div>Status</div>
                  <div>Source</div>
                  <div>Acquired</div>
                  <div>Actions</div>
                </div>
                {userCards.map((c) => (
                  <div key={c._id} style={cardRow}>
                    <div>
                      <div style={{ fontWeight: 600, color: "#222" }}>
                        {c.definition?.emoji ?? "🃏"}{" "}
                        {c.definition?.name ?? c.cardId}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#666" }}>
                        {c.definition?.rarity ?? "?"} · {c.cardId}
                      </div>
                    </div>
                    <div>
                      <span style={statusChip(c.status)}>{c.status}</span>
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#444" }}>{c.source}</div>
                    <div style={{ fontSize: "0.78rem", color: "#444" }}>
                      {formatDate(c.acquiredAt)}
                    </div>
                    <div>
                      <button
                        type="button"
                        style={dangerBtn}
                        onClick={() => handleRemove(c)}
                        disabled={removeState.isLoading}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      ) : null}
    </>
  );
};

export default UsersTab;
